/* eslint-disable security/detect-object-injection */
/* eslint-disable -eslint/no-explicit-any */
import {isFeatureEnabled} from 'feature-flags';
import {Box, Spinner} from 'platform/foundation';

import {ReactNode, ReactElement} from 'react';
import {useWatch} from 'react-hook-form';

import {BaseVehicle} from '../../../core/types/BaseVehicle';
import {VehicleVariantType} from '../../../core/types/VehicleVariantType';
import {useVehicleFormContext} from '../../hooks/useVehicleFormContext';

// TODO-SF: Improve typings when Sale / Rental / Service are supported (currently only BaseVehicle is supported) - https://carvago.atlassian.net/browse/T20-85486

// Base props
interface BaseShowWhenProps {
  children: ReactNode;
  fallback?: ReactNode;
  whenFeatureEnabled?: string;
  whenFeatureDisabled?: string;
  when?: boolean;
}

// Type helper to extract the type of a field from BaseVehicle
type FieldType<K extends keyof BaseVehicle> = BaseVehicle[K];

// Type helper to create an object type from field names
type WatchedFields<T extends readonly (keyof BaseVehicle)[]> = {
  [K in T[number]]: BaseVehicle[K];
};

/**
 * ShowWhen primitive - conditional rendering based on form values or vehicle variants
 *
 * @example
 * // Simple field/value matching
 * <ShowWhen name="fuelType" value="electric">
 *   <Field name="batteryCapacity" />
 * </ShowWhen>
 *
 * // Single field with condition
 * <ShowWhen name="power" condition={(value) => value > 100}>
 *   <Field name="highPowerNote" />
 * </ShowWhen>
 *
 * // Condition with all form values
 * <ShowWhen condition={(values) => values.power > 100}>
 *   <Field name="highPowerNote" />
 * </ShowWhen>
 *
 * // Condition with specific watched fields
 * <ShowWhen
 *   watchFields={['fuelType', 'power'] as const}
 *   condition={(values) => values.fuelType?.includes('FUELTYPE_ELECTRIC') && values.power > 100}
 * >
 *   <Field name="electricHighPowerNote" />
 * </ShowWhen>
 *
 * // Vehicle variant conditions
 * <ShowWhen vehicleVariants={['sale']}>
 *   <SaleVehicleOnlyFields />
 * </ShowWhen>
 *
 * <ShowWhen vehicleVariants={['sale', 'rental']}>
 *   <SaleOrRentalFields />
 * </ShowWhen>
 *
 * // Combine vehicle variants with form conditions
 * <ShowWhen
 *   vehicleVariants={['sale']}
 *   name="fuelType"
 *   value="FUELTYPE_ELECTRIC"
 * >
 *   <ElectricSaleVehicleFields />
 * </ShowWhen>
 *
 * // Feature flag conditions
 * <ShowWhen whenFeatureEnabled="NEW_VEHICLE_FORM">
 *   <NewFormFields />
 * </ShowWhen>
 *
 * <ShowWhen whenFeatureDisabled="OLD_FORM_LAYOUT">
 *   <LegacyFormFields />
 * </ShowWhen>
 *
 * // Combine feature flags with other conditions
 * <ShowWhen
 *   whenFeatureEnabled="ELECTRIC_FEATURES"
 *   name="fuelType"
 *   value="FUELTYPE_ELECTRIC"
 * >
 *   <AdvancedElectricFields />
 * </ShowWhen>
 *
 * // Permission-based conditions
 * <ShowWhen when={canReadBusinessCase}>
 *   <BusinessCaseFields />
 * </ShowWhen>
 *
 * // Combine permissions with feature flags and vehicle variants
 * <ShowWhen
 *   when={canReadBusinessCase}
 *   whenFeatureEnabled="ACL_SALES"
 *   vehicleVariants={['sale']}
 * >
 *   <SalesOnlyFields />
 * </ShowWhen>
 */

// Function overloads for better type inference
export function ShowWhen<K extends keyof BaseVehicle>(
  props: BaseShowWhenProps & {
    name: K;
    value: FieldType<K> extends (infer U)[] ? U | U[] : FieldType<K>;
    vehicleVariants?: VehicleVariantType[];
  }
): ReactElement;

export function ShowWhen<K extends keyof BaseVehicle>(
  props: BaseShowWhenProps & {
    name: K;
    condition: (value: FieldType<K>) => boolean;
    vehicleVariants?: VehicleVariantType[];
  }
): ReactElement;

export function ShowWhen<T extends readonly (keyof BaseVehicle)[]>(
  props: BaseShowWhenProps & {
    watchFields: T;
    condition: (values: WatchedFields<T>) => boolean;
    vehicleVariants?: VehicleVariantType[];
  }
): ReactElement;

export function ShowWhen(
  props: BaseShowWhenProps & {
    condition: (values: BaseVehicle) => boolean;
    vehicleVariants?: VehicleVariantType[];
  }
): ReactElement;

export function ShowWhen(
  props: BaseShowWhenProps & {
    vehicleVariants: VehicleVariantType[];
  }
): ReactElement;

export function ShowWhen(props: BaseShowWhenProps): ReactElement;

export function ShowWhen(
  props: BaseShowWhenProps & {
    when: boolean;
    vehicleVariants?: VehicleVariantType[];
  }
): ReactElement;

// Implementation
// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export function ShowWhen<K extends keyof BaseVehicle, T extends readonly (keyof BaseVehicle)[]>(
  props:
    | (BaseShowWhenProps & {
        name: K;
        value: FieldType<K> extends (infer U)[] ? U | U[] : FieldType<K>;
        vehicleVariants?: VehicleVariantType[];
      })
    | (BaseShowWhenProps & {
        name: K;
        condition: (value: FieldType<K>) => boolean;
        vehicleVariants?: VehicleVariantType[];
      })
    | (BaseShowWhenProps & {
        watchFields: T;
        condition: (values: WatchedFields<T>) => boolean;
        vehicleVariants?: VehicleVariantType[];
      })
    | (BaseShowWhenProps & {
        condition: (values: BaseVehicle) => boolean;
        vehicleVariants?: VehicleVariantType[];
      })
    | (BaseShowWhenProps & {vehicleVariants: VehicleVariantType[]})
    | (BaseShowWhenProps & {
        when: boolean;
        vehicleVariants?: VehicleVariantType[];
      })
    | BaseShowWhenProps
): ReactElement {
  const {children, fallback = null} = props;
  const {control, vehicleVariant} = useVehicleFormContext();

  const hasNameProperty = 'name' in props;
  const hasValueProperty = 'value' in props;
  const hasConditionProperty = 'condition' in props;
  const hasWatchFieldsProperty = 'watchFields' in props;
  const hasVehicleVariantsProperty = 'vehicleVariants' in props;
  const hasWhenProperty = 'when' in props;

  const isNameValueMode = hasNameProperty && hasValueProperty && !hasConditionProperty;
  const isNameConditionMode = hasNameProperty && hasConditionProperty;
  const isWatchFieldsConditionMode = hasWatchFieldsProperty && hasConditionProperty;
  const isAllValuesConditionMode =
    hasConditionProperty && !hasNameProperty && !hasWatchFieldsProperty;
  const isVehicleVariantsOnlyMode =
    hasVehicleVariantsProperty &&
    !hasNameProperty &&
    !hasConditionProperty &&
    !hasWatchFieldsProperty &&
    !hasWhenProperty;
  const isWhenMode =
    hasWhenProperty && !hasNameProperty && !hasConditionProperty && !hasWatchFieldsProperty;
  const isFeatureFlagOnlyMode =
    !hasNameProperty &&
    !hasConditionProperty &&
    !hasWatchFieldsProperty &&
    !hasVehicleVariantsProperty &&
    !hasWhenProperty;

  const isFeatureFlagViolation = Boolean(
    props.whenFeatureEnabled && !isFeatureEnabled(props.whenFeatureEnabled)
  );
  const isFeatureFlagDisabled = Boolean(
    props.whenFeatureDisabled && isFeatureEnabled(props.whenFeatureDisabled)
  );
  const isStaticWhenFalse = 'when' in props && props.when === false;

  // Always determine watch argument to satisfy React Hook rules
  let watchArg: K | T | undefined = undefined;

  if (isNameValueMode || isNameConditionMode) {
    watchArg = props.name;
  } else if (isWatchFieldsConditionMode) {
    watchArg = props.watchFields;
  } else if (isAllValuesConditionMode) {
    watchArg = undefined; // To watch all form values
  }

  const watchedData = useWatch({
    control,
    // name does not accept "undefined", using any to avoid type errors (avoidable by destructuring props, but not worth the effort)
    name: watchArg as any,
    disabled:
      isFeatureFlagOnlyMode ||
      isWhenMode ||
      isVehicleVariantsOnlyMode ||
      isFeatureFlagViolation ||
      isStaticWhenFalse ||
      isFeatureFlagDisabled,
  });

  // Check feature flags first
  if (isFeatureFlagViolation || isFeatureFlagDisabled) {
    return <>{fallback}</>;
  }

  // Check vehicle variants first
  let vehicleVariantMatches: boolean | null = true;
  if (hasVehicleVariantsProperty) {
    const {vehicleVariants} = props as {vehicleVariants: VehicleVariantType[]};

    if (vehicleVariant) {
      const hasRequiredVariants = vehicleVariants.some((variant) => {
        switch (variant) {
          case 'sale':
            return vehicleVariant.hasSaleVehicle;
          case 'rental':
            return vehicleVariant.hasRentalVehicle;
          case 'service':
            return vehicleVariant.hasServiceVehicle;
          default:
            return false;
        }
      });
      vehicleVariantMatches = hasRequiredVariants;
    } else {
      // If vehicle variant data is not available yet, don't show
      vehicleVariantMatches = null;
    }
  }

  if (vehicleVariantMatches === null) {
    return (
      <Box position="relative">
        {children}
        <Spinner variant="overlay" />
      </Box>
    );
  }

  // Check generic when condition
  if ('when' in props && props.when === false) {
    return <>{fallback}</>;
  }

  // If this is feature flag only mode, return early (feature flags already checked above)
  if (isFeatureFlagOnlyMode) {
    return <>{children}</>;
  }

  // If this is when mode, return early (when condition already checked above, vehicle variants checked above)
  if (isWhenMode) {
    return vehicleVariantMatches ? <>{children}</> : <>{fallback}</>;
  }

  // If this is vehicle variants only mode, return early
  if (isVehicleVariantsOnlyMode) {
    return vehicleVariantMatches ? <>{children}</> : <>{fallback}</>;
  }

  // If vehicle variants don't match, don't show regardless of other conditions
  if (!vehicleVariantMatches) {
    return <>{fallback}</>;
  }

  // Continue with existing form field logic
  let shouldShow = false;

  if (isNameValueMode) {
    const {value: configValue} = props as BaseShowWhenProps & {
      name: K;
      value: FieldType<K> extends (infer U)[] ? U | U[] : FieldType<K>;
    };
    const currentFieldValue = watchedData as FieldType<K>;

    if (Array.isArray(configValue)) {
      if (Array.isArray(currentFieldValue)) {
        shouldShow = configValue.every((itemInConfig) =>
          currentFieldValue.includes(itemInConfig as any)
        );
      } else {
        shouldShow = false;
      }
    } else {
      if (Array.isArray(currentFieldValue)) {
        shouldShow = currentFieldValue.includes(configValue as any);
      } else {
        shouldShow = currentFieldValue === configValue;
      }
    }
  } else if (isNameConditionMode) {
    const {condition} = props as BaseShowWhenProps & {
      name: K;
      condition: (value: FieldType<K>) => boolean;
    };
    shouldShow = condition(watchedData as FieldType<K>);
  } else if (isWatchFieldsConditionMode) {
    const {watchFields, condition} = props as BaseShowWhenProps & {
      watchFields: T;
      condition: (values: WatchedFields<T>) => boolean;
    };

    // When watchFields is used, watchedData is an array of values.
    // We need to reconstruct the object expected by the condition function.
    const valuesObject = watchFields.reduce((acc, fieldName, index) => {
      (acc as any)[fieldName] = (watchedData as any[])[index];
      return acc;
    }, {} as WatchedFields<T>);
    shouldShow = condition(valuesObject);
  } else if (isAllValuesConditionMode) {
    const {condition} = props as BaseShowWhenProps & {condition: (values: BaseVehicle) => boolean};
    shouldShow = condition(watchedData as BaseVehicle);
  }

  return shouldShow ? <>{children}</> : <>{fallback}</>;
}
